<template>
    <div class="calculation-overlay">
        <div class="calculation-overlay-content">
            <div v-if="!failed">
                <div class="calculation-overlay-icon">
                    <div class="spinner-border text-primary calculation-spinner" role="status"></div>
                </div>
                <h3 class="text-center mb-1">Připravujeme váš odhad</h3>
                <p class="text-center text-muted mb-4">Prosím vyčkejte, probíhá automatický výpočet</p>
                <div class="calculation-overlay-steps">
                    <div class="calculation-step" :class="stepClass(stepSendData, 0)">
                        <span class="calculation-step-icon">
                            <i class="fas fa-check-circle text-success" v-show="elapsedSeconds >= stepSendData"></i>
                            <span class="spinner-border spinner-border-sm text-primary" v-show="elapsedSeconds < stepSendData"></span>
                        </span>
                        <div>
                            <div class="calculation-step-label">Odesílání dat na server</div>
                            <div class="calculation-step-description">Ukládání parametrů odhadu</div>
                        </div>
                    </div>
                    <div class="calculation-step" :class="stepClass(stepEstateSelection, stepSendData)">
                        <span class="calculation-step-icon">
                            <i class="fas fa-check-circle text-success" v-show="elapsedSeconds >= stepEstateSelection"></i>
                            <span class="spinner-border spinner-border-sm text-primary" v-show="elapsedSeconds >= stepSendData && elapsedSeconds < stepEstateSelection"></span>
                            <i class="fas fa-circle text-muted calculation-step-pending-icon" v-show="elapsedSeconds < stepSendData"></i>
                        </span>
                        <div>
                            <div class="calculation-step-label">Výběr srovnávacích nemovitostí</div>
                            <div class="calculation-step-description">Vyhledávání a analýza podobných nemovitostí v okolí</div>
                        </div>
                    </div>
                    <div class="calculation-step" :class="stepClass(stepAnalysis, stepEstateSelection)">
                        <span class="calculation-step-icon">
                            <i class="fas fa-check-circle text-success" v-show="elapsedSeconds >= stepAnalysis"></i>
                            <span class="spinner-border spinner-border-sm text-primary" v-show="elapsedSeconds >= stepEstateSelection && elapsedSeconds < stepAnalysis"></span>
                            <i class="fas fa-circle text-muted calculation-step-pending-icon" v-show="elapsedSeconds < stepEstateSelection"></i>
                        </span>
                        <div>
                            <div class="calculation-step-label">Analýza a výpočet odhadu</div>
                            <div class="calculation-step-description">Porovnání parametrů a stanovení ceny</div>
                        </div>
                    </div>
                </div>
                <div class="calculation-overlay-footer">
                    <div class="calculation-elapsed">
                        <i class="fas fa-clock me-1"></i>
                        {{ formattedElapsedTime }}
                    </div>
                    <div class="calculation-estimate">
                        Výpočet obvykle trvá 1–2 minuty.<br>
                        Nezavírejte prosím tuto stránku.
                    </div>
                </div>
            </div>
            <div v-else>
                <div class="calculation-overlay-icon">
                    <i class="fas fa-exclamation-triangle text-warning calculation-failed-icon"></i>
                </div>
                <h3 class="text-center mb-1">Automatický výběr nemovitostí selhal</h3>
                <p class="text-center text-muted mb-4">
                    AI asistent nebyl schopen vybrat srovnávací nemovitosti.
                    Můžete to zkusit znovu, nebo pokračovat bez AI výběru.
                </p>
                <div class="d-flex flex-column gap-2">
                    <button class="btn btn-primary" @click="$emit('retry')" :disabled="retrying">
                        <span class="spinner-border spinner-border-sm me-1" v-show="retrying"></span>
                        <i class="fas fa-redo me-1" v-show="!retrying"></i>
                        Zkusit znovu s AI
                    </button>
                    <button class="btn btn-outline-secondary" @click="$emit('calculate-without-llm')" :disabled="retrying">
                        <i class="fas fa-calculator me-1"></i>
                        Vypočítat bez AI asistenta
                    </button>
                    <button class="btn btn-link text-muted" @click="$emit('close')" :disabled="retrying">
                        Zavřít
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {computed, defineComponent, onBeforeUnmount, ref, watch, toRef} from 'vue';

const STEP_SEND_DATA = 3;
const STEP_ESTATE_SELECTION = 25;
const STEP_ANALYSIS = 50;

export default defineComponent({
    props: {
        failed: {type: Boolean, default: false},
        retrying: {type: Boolean, default: false},
    },
    emits: ['retry', 'calculate-without-llm', 'close'],
    setup(props) {
        const elapsedSeconds = ref(0);
        let timer: number | null = null;

        function stopTimer(): void {
            if (timer !== null) {
                clearInterval(timer);
                timer = null;
            }
        }

        timer = window.setInterval(() => {
            elapsedSeconds.value++;
        }, 1000);

        watch(toRef(props, 'failed'), (failed) => {
            if (failed) {
                stopTimer();
            }
        });

        const formattedElapsedTime = computed(() => {
            const minutes = Math.floor(elapsedSeconds.value / 60);
            const seconds = elapsedSeconds.value % 60;
            return minutes > 0 ? `${minutes} min ${seconds} s` : `${seconds} s`;
        });

        function stepClass(doneAt: number, activeFrom: number): Record<string, boolean> {
            return {
                'is-done': elapsedSeconds.value >= doneAt,
                'is-active': elapsedSeconds.value >= activeFrom && elapsedSeconds.value < doneAt,
                'is-pending': elapsedSeconds.value < activeFrom,
            };
        }

        onBeforeUnmount(() => {
            stopTimer();
        });

        return {
            elapsedSeconds,
            formattedElapsedTime,
            stepSendData: STEP_SEND_DATA,
            stepEstateSelection: STEP_ESTATE_SELECTION,
            stepAnalysis: STEP_ANALYSIS,
            stepClass,
        };
    },
});
</script>
